/**
 * What the browser will actually parse, which is not the string as given. Per the URL standard, tab
 * and newline are stripped from anywhere in the input and leading C0 controls and spaces are
 * trimmed — so a scheme split by a tab, or padded with a leading space, still reaches the parser as
 * `javascript:`. Testing the raw string would let either walk straight past a scheme check.
 */
const asBrowserReads = (url: string) =>
  // eslint-disable-next-line no-control-regex
  url.replace(/[\t\n\r]/g, '').replace(/^[\u0000-\u0020]+/, '');

/**
 * Schemes that execute rather than navigate. `javascript:` is the reason this check exists —
 * assigning one to `location.href` runs it in the current origin, which for the consent screen is
 * the identity provider's.
 */
const isExecutableScheme = (url: string) => /^[a-z0-9+.-]*:/i.test(url) && !/^https?:/i.test(url);

/**
 * True for a URL this app is willing to hand to the browser: any same-origin reference, or an
 * absolute http(s) URL. Everything else — `javascript:`, `data:`, `vbscript:`, a protocol-relative
 * `//host` that silently leaves the origin — is refused.
 *
 * Same-origin covers more than an absolute path. A reference carrying no scheme and no authority —
 * `callback?code=x`, `./callback`, `?code=x`, `#frag` — resolves against the current document and
 * cannot leave the origin, so the last line admits it. Only the forms that *can* reach another
 * origin are enumerated above them.
 */
export const isSafeNavigationUrl = (rawUrl: string) => {
  const url = rawUrl ? asBrowserReads(rawUrl) : '';
  if (!url) {
    return false;
  }

  if (url.startsWith('\\')) {
    // A backslash is read as a slash while the parser is still deciding whether the URL has an
    // authority, so `\\host` and `\/host` land on another origin exactly as `//host` does. The
    // single-backslash form stays same-origin, but nothing here produces one, so the whole class
    // is refused rather than split by a rule that has to be re-derived to be trusted.
    return false;
  }

  if (url.startsWith('/')) {
    // `//host` and `/\host` are read as protocol-relative by browsers, not as local paths.
    return url[1] !== '/' && url[1] !== '\\';
  }

  return !isExecutableScheme(url);
};

/**
 * Full-page navigation, as an object so tests can replace it — assigning `window.location.href`
 * in a browser-based test navigates the test runner itself.
 *
 * The destination is checked first. Every caller today passes a URL the API returned, and the API
 * validates it before echoing it back, but that is a guarantee held one service away: this is the
 * one line that turns a bad string into script execution, so it carries its own guard. Returns
 * whether it navigated, so a caller can surface the refusal rather than appear to hang.
 */
const topLevelNavigation = {
  navigate: (url: string) => {
    if (!isSafeNavigationUrl(url)) {
      return false;
    }

    window.location.href = url;
    return true;
  },
};

export default topLevelNavigation;
